/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import './style.scss';

import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { reviewFinancialAdjustment } from 'apis/financialAdjustment.api';
import { toast } from 'layout';
import { ADJUSTMENT_STATUSES, ADJUSTMENT_TYPES, TOAST } from 'constant';
import {
  useCreateAdjustmentComment,
  useGetAllAdjustmentComments,
} from 'utils/hooks/adjustmentComments';
import { mapError } from 'utils/func';
import {
  adjustmentCommentsSchema,
  adjustmentRejectSchema,
} from 'features/SubmitProjectProposal/validation';
import Form from './Form';
import GeneralInfo from './GeneralInfo';
import RejectModal from '../RejectModal';
import ApproveModal from '../ApproveModal';
import AdjustmentComment from '../AdjustmentComment';

function FinancialAdjustmentForm({
  control,
  errors,
  handleSubmit,
  footerConfirmationPrompt,
  readOnly,
  isDirty,
  dirtyFields,
  reset,
  watch,
  setValue,
  project,
  financialAdjustmentId,
  financialAdjustment,
  setSchema,
  refetch,
  isSumUp,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isCommentVisible, setIsCommentVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);

  const isRejectedOrApproved = useMemo(
    () => financialAdjustment?.status === ADJUSTMENT_STATUSES.SUBMITTED ?? null,
    [financialAdjustment]
  );

  const { mutateAsync } = useCreateAdjustmentComment();
  const { mutateAsync: mutateAsyncReview } = useMutation(reviewFinancialAdjustment);

  const { data: adjustmentComments, refetch: refetchAdjustmentComments } =
    useGetAllAdjustmentComments(
      {
        adjustmentName: ADJUSTMENT_TYPES.FINANCIAL_ADJUSTMENT,
        adjustmentId: financialAdjustmentId,
      },
      {
        enabled: !!financialAdjustmentId,
      }
    );

  const onSubmitComment = async (data) => {
    const dataSender = {
      ...data,
      adjustmentName: ADJUSTMENT_TYPES.FINANCIAL_ADJUSTMENT,
      adjustmentId: financialAdjustmentId,
    };
    try {
      const res = await mutateAsync(dataSender);
      if (res?.status.toString().startsWith('2')) {
        toast(TOAST.SUCCESS, t('projectAdjustment.commentSuccess'));
        refetchAdjustmentComments();
        reset();
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const onSubmit = async (data) => {
    const dataSender = {};
    if (rejectModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.REJECTED;
      dataSender.rejectionReason = data?.rejectionReason;
    } else if (approveModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.APPROVED;
    }
    try {
      const res = await mutateAsyncReview({ id: financialAdjustmentId, body: dataSender });
      if (res?.status.toString().startsWith('2')) {
        toast(
          TOAST.SUCCESS,
          t(`projectAdjustment.${rejectModalVisible ? 'rejectModal' : 'approveModal'}.success`)
        );
        refetch();
        setSchema(adjustmentCommentsSchema);
        setRejectModalVisible(false);
        setApproveModalVisible(false);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const onCloseRejectModal = () => {
    setSchema(adjustmentCommentsSchema);
    reset({}, { keepValues: true });
    setRejectModalVisible(false);
  };

  return (
    <div className="bg-white p-5 financial-adjustment">
      <GeneralInfo data={project} financialAdjustmentId={financialAdjustmentId} />
      <Form
        errors={errors}
        control={control}
        footerConfirmationPrompt={footerConfirmationPrompt}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        watch={watch}
        setValue={setValue}
        readOnly={readOnly}
      />
      <div className="px-5 pb-2">
        <AdjustmentComment
          visible={isCommentVisible}
          onClose={() => setIsCommentVisible(false)}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          onSubmit={onSubmitComment}
          data={adjustmentComments}
          status={financialAdjustment?.status}
          disabled={!(isDirty && Object.keys(dirtyFields).length) || rejectModalVisible}
        />
      </div>

      <div
        className={`flex justify-content-end gap-4 ${
          !isSumUp || isRejectedOrApproved ? 'mt-6' : ''
        }`}
      >
        {!isSumUp && (
          <Button
            className="w-10rem"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            label={t('extendImplementationTime.comeBack')}
            severity="danger"
            type="button"
            onClick={() => {
              navigate(-1);
            }}
          />
        )}
        {isRejectedOrApproved && (
          <>
            <Button
              className="w-10rem"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              label={t('projectAdjustment.reject')}
              severity="warning"
              type="button"
              onClick={() => {
                setSchema(adjustmentRejectSchema);
                setRejectModalVisible(true);
              }}
              disabled={!isRejectedOrApproved}
            />
            <Button
              className="w-16rem"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              label={t('projectAdjustment.approve')}
              severity="success"
              type="button"
              onClick={() => setApproveModalVisible(true)}
              disabled={!isRejectedOrApproved}
            />
          </>
        )}
      </div>
      <RejectModal
        visible={rejectModalVisible}
        onClose={onCloseRejectModal}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />

      <ApproveModal
        visible={approveModalVisible}
        onClose={() => setApproveModalVisible(false)}
        onSubmit={onSubmit}
        isSubmitting={false}
      />
    </div>
  );
}

FinancialAdjustmentForm.propTypes = {
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  footerConfirmationPrompt: PropTypes.func,
  dirtyFields: PropTypes.object,
  isDirty: PropTypes.bool,
  reset: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  project: PropTypes.shape({}).isRequired,
  financialAdjustmentId: PropTypes.string,
  financialAdjustment: PropTypes.object,
  setSchema: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  isSumUp: PropTypes.bool,
};

FinancialAdjustmentForm.defaultProps = {
  readOnly: false,
  dirtyFields: {},
  isDirty: false,
  footerConfirmationPrompt: () => null,
  financialAdjustmentId: '',
  financialAdjustment: {},
  isSumUp: false,
};

export default FinancialAdjustmentForm;
